import { useEffect, useState } from "react";
import { Loader2, Pencil, Trash2, Plus, Check, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#64748b"];

function CatRow({ c, onSaved }) {
  const [edit, setEdit] = useState(false);
  const [nome, setNome] = useState(c.nome);
  const [colore, setColore] = useState(c.colore);
  const save = async () => {
    try { await api.put(`/studio/rubrica-studio/categorie/${c.id}`, { nome, colore }); setEdit(false); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async () => {
    if (!window.confirm(`Eliminare la categoria «${c.nome}»? I contatti resteranno senza categoria.`)) return;
    try { await api.delete(`/studio/rubrica-studio/categorie/${c.id}`); onSaved(); toast.success("Categoria eliminata"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex items-center gap-2 rounded-md border border-slate-800 px-2 py-1.5" data-testid={`rub-cat-row-${c.id}`}>
      {edit ? (
        <>
          <div className="flex gap-1">{COLORS.map((k) => (
            <button key={k} type="button" onClick={() => setColore(k)} className={`h-4 w-4 rounded-full ${colore === k ? "ring-2 ring-offset-1 ring-slate-400" : ""}`} style={{ background: k }} aria-label={k} />
          ))}</div>
          <input value={nome} onChange={(e) => setNome(e.target.value)} data-testid={`rub-cat-name-${c.id}`} className="h-8 flex-1 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm" />
          <button type="button" onClick={save} data-testid={`rub-cat-save-${c.id}`} className="text-emerald-500"><Check className="h-4 w-4" /></button>
          <button type="button" onClick={() => setEdit(false)} className="text-slate-400"><X className="h-4 w-4" /></button>
        </>
      ) : (
        <>
          <span className="h-3 w-3 rounded-full" style={{ background: c.colore }} />
          <span className="flex-1 text-sm">{c.nome}</span>
          <button type="button" onClick={() => setEdit(true)} data-testid={`rub-cat-edit-${c.id}`} className="text-slate-400 hover:text-amber-500"><Pencil className="h-4 w-4" /></button>
          <button type="button" onClick={del} data-testid={`rub-cat-del-${c.id}`} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
        </>
      )}
    </div>
  );
}

// Gestione categorie della rubrica dello studio (aggiungi, modifica, elimina).
export default function RubricaCategorieDialog({ open, onOpenChange, categorie, onChanged }) {
  const [nome, setNome] = useState("");
  const [colore, setColore] = useState(COLORS[0]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setNome(""); setColore(COLORS[0]); } }, [open]);
  const add = async () => {
    if (!nome.trim()) return;
    setBusy(true);
    try { await api.post("/studio/rubrica-studio/categorie", { nome: nome.trim(), colore }); setNome(""); onChanged(); toast.success("Categoria aggiunta"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-[#0f1420] text-slate-100 border border-slate-800" data-testid="rub-cat-dialog">
        <DialogHeader><DialogTitle>Categorie contatti</DialogTitle></DialogHeader>
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {categorie.map((c) => <CatRow key={c.id} c={c} onSaved={onChanged} />)}
          {!categorie.length && <p className="text-xs text-slate-500">Nessuna categoria.</p>}
        </div>
        <div className="flex items-center gap-2 border-t border-slate-800 pt-3">
          <div className="flex gap-1">{COLORS.slice(0, 5).map((k) => (
            <button key={k} type="button" onClick={() => setColore(k)} className={`h-4 w-4 rounded-full ${colore === k ? "ring-2 ring-offset-1 ring-slate-400" : ""}`} style={{ background: k }} aria-label={k} />
          ))}</div>
          <input value={nome} onChange={(e) => setNome(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Nuova categoria (es. Carrozzeria)"
            data-testid="rub-cat-new-name" className="h-9 flex-1 rounded-md border border-slate-700 bg-slate-900/60 px-2 text-sm" />
          <button type="button" onClick={add} disabled={busy || !nome.trim()} data-testid="rub-cat-add"
            className="h-9 px-3 inline-flex items-center gap-1 rounded-md bg-amber-500 text-[#0a0d14] text-sm font-semibold disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}Aggiungi
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
